/**
 * UpSynQ logo.
 *
 * Three variants, all sized off the surrounding font-size so the mark
 * scales with whatever Tailwind text-size class is passed in:
 *
 *   <Logo />                  → horizontal lockup (mark + wordmark)
 *   <Logo variant="mark" />   → brand mark only (tight contexts)
 *   <Logo variant="wordmark"/>→ text only (prose, footers)
 *
 * Why not just an <img src="…svg">?  Two reasons:
 *
 *   1.  currentColor on the wordmark.  The non-Q letters need to
 *       inherit the page's foreground colour so light/dark themes
 *       both read clearly.  <img> opaquely renders the SVG in its
 *       own colour context, so the mark works but currentColor on
 *       the wordmark won't track the parent.
 *   2.  No font race.  The wordmark renders in HTML, so it uses the
 *       page's already-loaded Inter font.  An SVG <text> element
 *       can repaint with a fallback if Inter loads after the SVG
 *       is decoded.
 *
 * The standalone `.svg` files in /public are for press-kit and
 * external-share use; they self-contain their own <text> + gradient
 * (and the wordmark uses the embedded gold gradient on the Q so it
 * looks identical on a white background).
 */
import { cn } from "@/lib/utils";

const GOLD_GRADIENT = "linear-gradient(135deg, #B8860B 0%, #E6B800 50%, #F8DE7E 100%)";

interface LogoProps {
  className?: string;
  /** Tailwind text-size class. Drives both wordmark + mark sizing. */
  size?: string;
  /** Lockup (default), mark-only, or wordmark-only. */
  variant?: "lockup" | "mark" | "wordmark";
}


/** The Q-as-node brand mark.  100-unit grid; identical geometry to
 *  /public/upsynq-mark.svg so the in-app mark and any externally
 *  exported file stay visually aligned. */
const Mark = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 100 100"
    role="img"
    aria-hidden="true"
    className={cn("shrink-0", className)}
  >
    <defs>
      <linearGradient id="upsynq-mark-grad" x1="10" y1="90" x2="90" y2="10" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#B8860B" />
        <stop offset=".5" stopColor="#E6B800" />
        <stop offset="1" stopColor="#F8DE7E" />
      </linearGradient>
    </defs>
    <g fill="none" stroke="url(#upsynq-mark-grad)" strokeWidth={10} strokeLinecap="round">
      <circle cx="40" cy="45" r="28" />
      <line x1="59.8" y1="64.8" x2="79.6" y2="84.6" />
    </g>
    <circle cx="79.6" cy="84.6" r="5.5" fill="url(#upsynq-mark-grad)" />
  </svg>
);


/** "UpSyn" in current foreground colour + a gradient-gold "Q". */
const Wordmark = () => (
  <span className="inline-flex items-baseline leading-none">
    <span className="text-foreground">UpSyn</span>
    <span
      className="bg-clip-text text-transparent"
      style={{ backgroundImage: GOLD_GRADIENT }}
    >
      Q
    </span>
  </span>
);


export const Logo = ({ className, size = "text-2xl", variant = "lockup" }: LogoProps) => {
  if (variant === "mark") {
    return (
      <span
        aria-label="UpSynQ"
        className={cn("inline-flex items-center select-none", size, className)}
      >
        <Mark className="h-[1.4em] w-[1.4em]" />
      </span>
    );
  }

  if (variant === "wordmark") {
    return (
      <span
        aria-label="UpSynQ"
        className={cn("inline-flex items-baseline font-bold tracking-tight select-none", size, className)}
      >
        <Wordmark />
      </span>
    );
  }

  // Lockup.  Mark height is tuned to roughly the wordmark cap height
  // (≈ 1.15em on the Inter we're using); a 4-px gap (gap-1.5) reads
  // as one airy unit at sidebar font sizes and tightens visually at
  // larger sizes without re-tuning.
  return (
    <span
      aria-label="UpSynQ"
      className={cn(
        "inline-flex items-center gap-1.5 font-bold tracking-tight select-none",
        size,
        className,
      )}
    >
      <Mark className="h-[1.15em] w-[1.15em]" />
      <Wordmark />
    </span>
  );
};
